/**
 * Chain bootstrap — fetches chain config from the backend at app boot time
 * and builds wagmi/RainbowKit configuration dynamically.
 *
 * Must be called (and awaited) before any module imports wagmi.ts.
 * No fallbacks — if the API is unreachable the app will not boot.
 */

import type { Chain, Transport } from 'viem';
import { http } from 'wagmi';

// Matches the backend WagmiChainConfig response shape
interface WagmiChainConfig {
  id: number;
  name: string;
  displayName: string;
  nativeCurrency: { name: string; symbol: string; decimals: number };
  rpcUrls: string[];
  blockExplorerUrl?: string;
  iconUrl?: string;
  testnet: boolean;
}

interface BootstrapResult {
  chains: [Chain, ...Chain[]];
  transports: Record<number, Transport>;
}

let cached: BootstrapResult | null = null;

function toViemChain(cfg: WagmiChainConfig): Chain {
  return {
    id: cfg.id,
    name: cfg.displayName,
    nativeCurrency: cfg.nativeCurrency,
    rpcUrls: {
      default: { http: cfg.rpcUrls },
    },
    blockExplorers: cfg.blockExplorerUrl
      ? { default: { name: 'Explorer', url: cfg.blockExplorerUrl } }
      : undefined,
    testnet: cfg.testnet || undefined,
  } as Chain;
}

/**
 * Fetch chain config from the backend and cache the result.
 * Throws on failure — the app cannot boot without chain data.
 */
export async function bootstrapChains(): Promise<BootstrapResult> {
  if (cached) return cached;

  const gatewayUrl =
    (import.meta.env.VITE_API_GATEWAY_URL as string) ||
    'https://api.integraledger.net';

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 5000);

  const res = await fetch(`${gatewayUrl}/v1/networks/wagmi-config`, {
    signal: controller.signal,
  });
  clearTimeout(timeout);

  if (!res.ok) {
    throw new Error(`Chain bootstrap failed: HTTP ${res.status}`);
  }

  const data: WagmiChainConfig[] = await res.json();
  if (!Array.isArray(data) || data.length === 0) {
    throw new Error('Chain bootstrap failed: empty or invalid response');
  }

  const chains = data.map(toViemChain) as [Chain, ...Chain[]];
  const transports: Record<number, Transport> = {};
  for (const cfg of data) {
    transports[cfg.id] = http(cfg.rpcUrls[0]);
  }

  cached = { chains, transports };
  return cached;
}

/**
 * Synchronous accessor — returns the cached bootstrap result.
 * Throws if bootstrapChains() hasn't been awaited yet.
 */
export function getBootstrapResult(): BootstrapResult {
  if (!cached) {
    throw new Error(
      'getBootstrapResult() called before bootstrapChains() completed. ' +
      'Ensure bootstrapChains() is awaited before importing wagmi.ts.'
    );
  }
  return cached;
}
